import { Mode } from 'mk2/containers/PhoneVerification/PhoneVerification.constants';
import { APIFailureAction, APIRequestAction, APISuccessAction } from 'mk2/helpers/action';
import { Action } from 'redux';

export interface PhoneVerificationSendSmsAction extends Action {
    readonly prefix: string;
    readonly phoneNumber: string;
    readonly mode: Mode;
}

export interface PhoneVerificationSendSmsResponse {
    readonly prefix: string;
    readonly phoneNumber: string;
}

export const PHONE_VERIFICATION_SEND_SMS_REQUEST = 'PHONE_VERIFICATION_SEND_SMS_REQUEST';

export interface PhoneVerificationSendSmsRequestAction extends PhoneVerificationSendSmsAction, APIRequestAction {
    readonly type: typeof PHONE_VERIFICATION_SEND_SMS_REQUEST;
}

export const PHONE_VERIFICATION_SEND_SMS_SUCCESS = 'PHONE_VERIFICATION_SEND_SMS_SUCCESS';

export interface PhoneVerificationSendSmsSuccessAction extends PhoneVerificationSendSmsAction {
    readonly type: typeof PHONE_VERIFICATION_SEND_SMS_SUCCESS;
    readonly response: PhoneVerificationSendSmsResponse;
}

export const PHONE_VERIFICATION_SEND_SMS_FAILURE = 'PHONE_VERIFICATION_SEND_SMS_FAILURE';

export interface PhoneVerificationSendSmsFailureAction extends PhoneVerificationSendSmsAction, APIFailureAction {
    readonly type: typeof PHONE_VERIFICATION_SEND_SMS_FAILURE;
    readonly limitReached: boolean;
}

export const phoneVerificationSendSms = {
    request: (prefix: string, phoneNumber: string, mode: Mode): PhoneVerificationSendSmsRequestAction => ({
        type: PHONE_VERIFICATION_SEND_SMS_REQUEST,
        prefix, phoneNumber, mode,
    }),
    success: (prefix: string, phoneNumber: string, mode: Mode, response: PhoneVerificationSendSmsResponse): PhoneVerificationSendSmsSuccessAction => ({
        type: PHONE_VERIFICATION_SEND_SMS_SUCCESS,
        response,
        prefix, phoneNumber, mode,
    }),
    failure: (prefix: string, phoneNumber: string, mode: Mode, limitReached: boolean, error: any): PhoneVerificationSendSmsFailureAction => ({
        type: PHONE_VERIFICATION_SEND_SMS_FAILURE,
        error,
        prefix, phoneNumber, mode, limitReached,
    }),
};

export interface PhoneVerificationSaveCallMeAction extends Action {
    readonly prefix: string;
    readonly phoneNumber: string;
    readonly mode: Mode;
}

export const PHONE_VERIFICATION_SAVE_CALL_ME_REQUEST = 'PHONE_VERIFICATION_SAVE_CALL_ME_REQUEST';

export interface PhoneVerificationSaveCallMeRequestAction extends PhoneVerificationSaveCallMeAction, APIRequestAction {
    readonly type: typeof PHONE_VERIFICATION_SAVE_CALL_ME_REQUEST;
}

export const PHONE_VERIFICATION_SAVE_CALL_ME_SUCCESS = 'PHONE_VERIFICATION_SAVE_CALL_ME_SUCCESS';

export interface PhoneVerificationSaveCallMeSuccessAction extends PhoneVerificationSaveCallMeAction, APISuccessAction {
    readonly type: typeof PHONE_VERIFICATION_SAVE_CALL_ME_SUCCESS;
}

export const PHONE_VERIFICATION_SAVE_CALL_ME_FAILURE = 'PHONE_VERIFICATION_SAVE_CALL_ME_FAILURE';

export interface PhoneVerificationSaveCallMeFailureAction extends PhoneVerificationSaveCallMeAction, APIFailureAction {
    readonly type: typeof PHONE_VERIFICATION_SAVE_CALL_ME_FAILURE;
}

export const phoneVerificationSaveCallMe = {
    request: (prefix: string, phoneNumber: string, mode: Mode): PhoneVerificationSaveCallMeRequestAction => ({
        type: PHONE_VERIFICATION_SAVE_CALL_ME_REQUEST,
        prefix, phoneNumber, mode,
    }),
    success: (prefix: string, phoneNumber: string, mode: Mode, response: any): PhoneVerificationSaveCallMeSuccessAction => ({
        type: PHONE_VERIFICATION_SAVE_CALL_ME_SUCCESS,
        response,
        prefix, phoneNumber, mode,
    }),
    failure: (prefix: string, phoneNumber: string, mode: Mode, error: any): PhoneVerificationSaveCallMeFailureAction => ({
        type: PHONE_VERIFICATION_SAVE_CALL_ME_FAILURE,
        error,
        prefix, phoneNumber, mode,
    }),
};

export interface PhoneVerificationSaveTokenAction extends Action {
    readonly token: string;
    readonly mode: Mode;
}

export const PHONE_VERIFICATION_SAVE_TOKEN_REQUEST = 'PHONE_VERIFICATION_SAVE_TOKEN_REQUEST';

export interface PhoneVerificationSaveTokenRequestAction extends PhoneVerificationSaveTokenAction, APIRequestAction {
    readonly type: typeof PHONE_VERIFICATION_SAVE_TOKEN_REQUEST;
}

export const PHONE_VERIFICATION_SAVE_TOKEN_SUCCESS = 'PHONE_VERIFICATION_SAVE_TOKEN_SUCCESS';

export interface PhoneVerificationSaveTokenSuccessAction extends PhoneVerificationSaveTokenAction {
    readonly type: typeof PHONE_VERIFICATION_SAVE_TOKEN_SUCCESS;
}

export const PHONE_VERIFICATION_SAVE_TOKEN_FAILURE = 'PHONE_VERIFICATION_SAVE_TOKEN_FAILURE';

export interface PhoneVerificationSaveTokenFailureAction extends PhoneVerificationSaveTokenAction, APIFailureAction {
    readonly type: typeof PHONE_VERIFICATION_SAVE_TOKEN_FAILURE;
}

export const phoneVerificationSaveToken = {
    request: (token: string, mode: Mode): PhoneVerificationSaveTokenRequestAction => ({
        type: PHONE_VERIFICATION_SAVE_TOKEN_REQUEST,
        token, mode,
    }),
    success: (token: string, mode: Mode): PhoneVerificationSaveTokenSuccessAction => ({
        type: PHONE_VERIFICATION_SAVE_TOKEN_SUCCESS,
        token, mode,
    }),
    failure: (token: string, mode: Mode, error: any): PhoneVerificationSaveTokenFailureAction => ({
        type: PHONE_VERIFICATION_SAVE_TOKEN_FAILURE,
        error,
        token, mode,
    }),
};

// Triggers
export const PHONE_VERIFICATION_REQUEST_TOKEN = 'PHONE_VERIFICATION_REQUEST_TOKEN';

export interface PhoneVerificationRequestTokenAction extends PhoneVerificationSendSmsAction {
    readonly type: typeof PHONE_VERIFICATION_REQUEST_TOKEN;
}

export const phoneVerificationRequestToken = (prefix: string, phoneNumber: string, mode: Mode): PhoneVerificationRequestTokenAction => ({
    type: PHONE_VERIFICATION_REQUEST_TOKEN,
    prefix, phoneNumber, mode,
});

export const PHONE_VERIFICATION_REQUEST_CALL = 'PHONE_VERIFICATION_REQUEST_CALL';

export interface PhoneVerificationRequestCallAction extends PhoneVerificationSaveCallMeAction {
    readonly type: typeof PHONE_VERIFICATION_REQUEST_CALL;
}

export const phoneVerificationRequestCall = (prefix: string, phoneNumber: string, mode: Mode): PhoneVerificationRequestCallAction => ({
    type: PHONE_VERIFICATION_REQUEST_CALL,
    prefix, phoneNumber, mode,
});

export const PHONE_VERIFICATION_GO_TO = 'PHONE_VERIFICATION_GO_TO';

export interface PhoneVerificationGoToAction extends Action {
    readonly type: typeof PHONE_VERIFICATION_GO_TO;
    readonly step: number;
}

export const phoneVerificationGoTo = (step: number): PhoneVerificationGoToAction => ({
    type: PHONE_VERIFICATION_GO_TO,
    step,
});

export const PHONE_VERIFICATION_VERIFY_TOKEN = 'PHONE_VERIFICATION_VERIFY_TOKEN';

export interface PhoneVerificationVerifyTokenAction extends PhoneVerificationSaveTokenAction {
    readonly type: typeof PHONE_VERIFICATION_VERIFY_TOKEN;
    readonly fullPhoneNumber: string;
}

export const phoneVerificationVerifyToken = (token: string, mode: Mode, fullPhoneNumber: string): PhoneVerificationVerifyTokenAction => ({
    type: PHONE_VERIFICATION_VERIFY_TOKEN,
    token, mode, fullPhoneNumber,
});

export const PHONE_VERIFICATION_RESET = 'PHONE_VERIFICATION_RESET';

export interface PhoneVerificationResetAction extends Action {
    readonly type: typeof PHONE_VERIFICATION_RESET;
}

export const phoneVerificationReset = (): PhoneVerificationResetAction => ({ type: PHONE_VERIFICATION_RESET });
